import { createClient } from "@/lib/supabase/client";
import type { BlogPostReaction, ReactionType } from "@/lib/supabase/types";
import { emojiToReaction, getClientId } from "@/lib/utils";

export async function getPostReactions(
	postId: string,
): Promise<BlogPostReaction[] | null> {
	const supabase = await createClient();

	const { data: updatedReactions, error } = await supabase
		.from("blog_post_reactions")
		.select("*")
		.eq("post_id", postId);

	if (error) {
		console.error("Error fetching updated reactions:", error);
		return null;
	}

	if (!updatedReactions) {
		console.error("No updated reactions found");
		return null;
	}
	return updatedReactions;
}

export async function getReactionTypes(): Promise<ReactionType[] | null> {
	const supabase = await createClient();

	const { data: reactionTypes, error } = await supabase
		.from("reaction_types")
		.select("*");

	if (error) {
		console.error("Error fetching reaction types:", error);
		return null;
	}

	if (!reactionTypes) {
		console.error("No reaction types found");
		return null;
	}
	return reactionTypes;
}

export async function updatePostReactions(
	postId: string,
	userId: string | null,
	emoji: string,
) {
	const supabase = await createClient();

	const reactionName = emojiToReaction(emoji);
	const clientId = getClientId();

	const { data: reaction, error: reactionError } = await supabase
		.from("reaction_types")
		.select("*")
		.eq("name", reactionName)
		.single();

	if (reactionError) {
		return { error: reactionError };
	}

	const { error: reactionInsertError } = await supabase
		.from("blog_post_reactions")
		.insert({
			post_id: postId,
			reaction_type_id: reaction.id,
			client_id: clientId,
			user_id: userId,
		});
	return { success: true, error: reactionInsertError };
}

export async function getUserReactions(userId: string) {
	const supabase = await createClient();
	const { data, error } = await supabase
		.from("blog_post_reactions")
		.select("*")
		.eq("user_id", userId)
		.overrideTypes<BlogPostReaction[]>();
	return { data, error };
}

export async function deleteReaction(userId: string, reactionId: string) {
	const supabase = await createClient();
	const { error } = await supabase
		.from("blog_post_reactions")
		.delete()
		.eq("user_id", userId)
		.eq("id", reactionId);
	return { error };
}
